import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

const toFalse = () => false;
const canUpdateEmail = ({ isEditMode, canUpdateVoucher }) => isEditMode && canUpdateVoucher;
const canUpdateStatus = ({ isEditMode, canDisableVoucher, canEnableVoucher }) => isEditMode && (canDisableVoucher || canEnableVoucher);
const canUpdateSalesDate = ({ isEditMode, canUpdateVoucher, voucherStatus }) =>
  isEditMode && canUpdateVoucher && voucherStatus !== 'REDEEMED';

const otherVoucherEditSettings = {
  email: {
    ACTIVE: canUpdateEmail,
    EXPIRED: toFalse,
    REDEEMED: canUpdateEmail,
    INACTIVE: canUpdateEmail
  },
  status: {
    ACTIVE: canUpdateStatus,
    EXPIRED: toFalse,
    REDEEMED: toFalse,
    INACTIVE: canUpdateStatus
  },
  salesDate: {
    ACTIVE: canUpdateSalesDate,
    EXPIRED: toFalse,
    REDEEMED: toFalse,
    INACTIVE: canUpdateSalesDate
  }
};

export const VoucherEditSettingsContext = React.createContext();

export const VoucherEditSettings = ({
  children,
  voucherType,
  voucherStatus,
  isEditMode,
  canUpdateVoucher,
  canDisableVoucher,
  canEnableVoucher
}) => {
  const editSettings = useMemo(() => {
    const options = {
      voucherType,
      voucherStatus,
      isEditMode,
      canEnableVoucher,
      canDisableVoucher,
      canUpdateVoucher
    };

    const fieldSettings = Object.keys(otherVoucherEditSettings).reduce(
      (acc, key) => ({
        ...acc,
        [key]: !!otherVoucherEditSettings[key][voucherStatus](options)
      }),
      {}
    );

    const formSettings = {
      isVoucherEditable: canEnableVoucher || canDisableVoucher || canUpdateVoucher
    };

    return {
      fields: fieldSettings,
      form: formSettings
    };
  }, [voucherType, voucherStatus, isEditMode, canEnableVoucher, canDisableVoucher, canUpdateVoucher]);

  return <VoucherEditSettingsContext.Provider value={editSettings}>{children}</VoucherEditSettingsContext.Provider>;
};

export const useVoucherEditSettings = () => {
  const editSettings = React.useContext(VoucherEditSettingsContext);

  return {
    fields: editSettings.fields,
    form: editSettings.form
  };
};

VoucherEditSettings.propTypes = {
  voucherType: PropTypes.string.isRequired,
  voucherStatus: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  canUpdateVoucher: PropTypes.bool.isRequired,
  canDisableVoucher: PropTypes.bool.isRequired,
  canEnableVoucher: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired
};
